import { installationDates } from './installationCalendarState';

const POLISH_MONTHS = [
  'stycznia','lutego','marca','kwietnia','maja','czerwca',
  'lipca','sierpnia','września','października','listopada','grudnia',
];
const POLISH_DAYS = ['Poniedziałek','Wtorek','Środa','Czwartek','Piątek','Sobota','Niedziela'];

function formatDate(dateStr) {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return `${d.getDate()} ${POLISH_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function formatDateShort(dateStr) {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  const dayName = POLISH_DAYS[d.getDay() === 0 ? 6 : d.getDay() - 1];
  return `${dayName}, ${d.getDate()} ${POLISH_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function esc(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function printHtmlInFrame(html, title = 'Wydruk') {
  const iframe = document.createElement('iframe');
  iframe.title = title;
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '1px',
    height: '1px',
    border: '0',
    opacity: '0',
    pointerEvents: 'none',
  });

  let cleanupTimer;
  let cleanedUp = false;
  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    window.clearTimeout(cleanupTimer);
    iframe.remove();
  };

  iframe.onload = () => {
    const printWindow = iframe.contentWindow;
    const printDocument = iframe.contentDocument;
    if (!printWindow || !printDocument) {
      cleanup();
      return;
    }

    const openPrintDialog = () => {
      if (cleanedUp) return;
      printWindow.addEventListener('afterprint', cleanup, { once: true });
      printWindow.focus();
      printWindow.print();
      // Some browsers do not dispatch afterprint for iframe documents.
      if (!cleanedUp) cleanupTimer = window.setTimeout(cleanup, 120000);
    };

    const fontsReady = printDocument.fonts?.ready;
    if (fontsReady) {
      fontsReady.then(() => window.setTimeout(openPrintDialog, 50)).catch(openPrintDialog);
    } else {
      window.setTimeout(openPrintDialog, 50);
    }
  };

  iframe.srcdoc = html;
  document.body.appendChild(iframe);
}

function getInstallerNamesForDay(installation, teams = [], installers = [], day, teamId = null) {
  const assignments = (installation.assignments || []).filter((a) => a.assigned_date === day);
  const people = [];
  const seenIds = new Set();

  function addPerson(id, name) {
    if (id && seenIds.has(id)) return;
    if (id) seenIds.add(id);
    if (name) people.push(name);
  }

  assignments
    .filter((assignment) => teamId == null || assignment.team_id === teamId)
    .forEach((assignment) => {
    if (assignment.team_id) {
      const team = teams.find((t) => t.id === assignment.team_id);
      if (!assignment.installer_id && team?.members?.length) {
        team.members.forEach((member) => {
          addPerson(member.id, `${member.first_name || ''} ${member.last_name || ''}`.trim());
        });
        return;
      }
    }

    if (assignment.installer_id) {
      const installer = installers.find((i) => i.id === assignment.installer_id);
      const fallbackName = installer
        ? `${installer.first_name || ''} ${installer.last_name || ''}`.trim()
        : assignment.installer_name;
      addPerson(assignment.installer_id, assignment.installer_name || fallbackName);
    } else if (assignment.installer_name) {
      addPerson(null, assignment.installer_name);
    }
    });

  return people;
}

function getInstallationWh(installation) {
  const directWh = (installation?.wh || '').trim();
  if (directWh) return directWh;
  return [...new Set(
    (installation?.items || [])
      .map((item) => (item.wh || '').trim())
      .filter(Boolean)
  )].join(', ');
}

function getTeamLeader(team) {
  if (!team?.members?.length) return '';
  const membersByRank = [...team.members].sort((left, right) =>
    (left.position_rank ?? 9999) - (right.position_rank ?? 9999)
  );
  const explicitLeader = membersByRank.find((member) =>
    String(member.position_name || member.position || '').toLocaleLowerCase('pl-PL').includes('brygadz')
  );
  const leader = explicitLeader || membersByRank[0];
  return `${leader.first_name || ''} ${leader.last_name || ''}`.trim();
}

function getDailyPrintContext(installation, teams, installers, day, sourceIndex, teamAssignment = null) {
  const assignments = (installation.assignments || []).filter((assignment) => assignment.assigned_date === day);
  const teamId = teamAssignment?.team_id ?? null;
  const team = teamAssignment
    ? teams.find((candidate) => candidate.id === teamId)
    : null;
  const people = getInstallerNamesForDay(installation, teams, installers, day, teamId);
  const individualIds = assignments
    .filter((assignment) => !assignment.team_id)
    .map((assignment) => assignment.installer_id || assignment.installer_name || '')
    .sort()
    .join('|');
  const groupKey = teamId != null
    ? `team:${teamId}:${people.join('|')}`
    : `individual:${individualIds || 'unassigned'}`;
  const teamIndex = team ? teams.findIndex((candidate) => candidate.id === team.id) : -1;
  const displayOrder = teamAssignment?.display_order
    ?? (assignments.length
      ? Math.min(...assignments.map((assignment) => assignment.display_order ?? 0))
      : 0);

  return {
    installation,
    groupKey,
    groupOrder: teamIndex < 0 ? teams.length : teamIndex,
    groupLabel: team?.name || teamAssignment?.team_name || people.join(', ') || 'Nieprzypisane',
    displayOrder,
    assignmentId: teamAssignment?.id ?? assignments[0]?.id ?? 0,
    sourceIndex,
    people,
    leader: getTeamLeader(team),
  };
}

function getDailyPrintContexts(installation, teams, installers, day, sourceIndex) {
  const assignments = (installation.assignments || []).filter(
    (assignment) => assignment.assigned_date === day
  );
  const regularTeamAssignments = assignments.filter((assignment) => {
    if (!assignment.team_id) return false;
    return !teams.find((team) => team.id === assignment.team_id)?.is_special_truck;
  });
  const eligibleTeamAssignments = regularTeamAssignments.length
    ? regularTeamAssignments
    : assignments.filter((assignment) => assignment.team_id);
  const assignmentByTeam = new Map();

  eligibleTeamAssignments.forEach((assignment) => {
    const current = assignmentByTeam.get(assignment.team_id);
    if (!current
      || (assignment.display_order ?? 0) < (current.display_order ?? 0)
      || ((assignment.display_order ?? 0) === (current.display_order ?? 0)
        && (assignment.id ?? 0) < (current.id ?? 0))) {
      assignmentByTeam.set(assignment.team_id, assignment);
    }
  });

  if (!assignmentByTeam.size) {
    return [getDailyPrintContext(installation, teams, installers, day, sourceIndex)];
  }

  return [...assignmentByTeam.values()].map((teamAssignment) =>
    getDailyPrintContext(
      installation,
      teams,
      installers,
      day,
      sourceIndex,
      teamAssignment
    )
  );
}

export function printDailyInstallationSummary(installations = [], teams = [], installers = [], day, variant = 'general') {
  if (!day) return;

  const dayInstallations = installations.filter((installation) => {
    if (!installation?.planned_start_date) return false;
    return installationDates(installation).includes(day);
  });
  const dailyRows = dayInstallations
    .flatMap((installation, sourceIndex) =>
      getDailyPrintContexts(installation, teams, installers, day, sourceIndex)
    )
    .sort((left, right) => left.groupOrder - right.groupOrder
      || left.groupLabel.localeCompare(right.groupLabel, 'pl')
      || left.displayOrder - right.displayOrder
      || left.assignmentId - right.assignmentId
      || left.sourceIndex - right.sourceIndex);

  const groupedRows = [];
  dailyRows.forEach((row) => {
    const lastGroup = groupedRows[groupedRows.length - 1];
    if (lastGroup?.key === row.groupKey) lastGroup.rows.push(row);
    else groupedRows.push({ key: row.groupKey, rows: [row] });
  });

  let printedIndex = 0;
  const isWarehouse = variant === 'warehouse';
  const rowsHtml = groupedRows.length
    ? groupedRows.map((group) => group.rows.map((row, groupIndex) => {
        printedIndex += 1;
        const installation = row.installation;
        const crewCell = groupIndex === 0
          ? `<td class="crew" rowspan="${group.rows.length}">${isWarehouse
              ? (esc(row.leader) || '<span class="muted">Brak brygadzisty</span>')
              : (row.people.length ? esc(row.people.join(', ')) : '<span class="muted">Brak przypisanych montażystów</span>')
            }</td>`
          : '';
        return `
          <tr class="${groupIndex === 0 ? 'group-start' : ''}">
            <td class="index">${printedIndex}</td>
            <td class="order">${esc(installation.calendar_status === 'calendar_note'
              ? installation.calendar_note_title || installation.notes || 'Notatka'
              : installation.order_number) || '<span class="muted">-</span>'}</td>
            <td>${esc(installation.contractor) || '<span class="muted">-</span>'}</td>
            ${crewCell}
            ${isWarehouse ? `<td>${esc(getInstallationWh(installation)) || '<span class="muted">-</span>'}</td>` : ''}
          </tr>`;
      }).join('')).join('')
    : `<tr><td colspan="${isWarehouse ? 5 : 4}" class="empty">Brak zleceń montażowych w tym dniu</td></tr>`;

  const now = new Date();
  const generatedAt = `${now.getDate()} ${POLISH_MONTHS[now.getMonth()]} ${now.getFullYear()}, ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;

  const html = `<!DOCTYPE html>
<html lang="pl">
<head>
  <meta charset="UTF-8">
  <title>Plan montaży ${esc(day)}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 10pt; }
    .page { padding: 8mm 9mm; }
    .header { display: flex; justify-content: space-between; align-items: flex-end; gap: 10mm; margin-bottom: 5mm; border-bottom: 2px solid #111; padding-bottom: 3mm; }
    h1 { margin: 0; font-size: 16pt; line-height: 1.1; }
    .date { font-size: 11pt; font-weight: bold; margin-top: 2mm; }
    .meta { font-size: 7.5pt; color: #666; text-align: right; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    th { background: #111; color: #fff; font-size: 8.5pt; text-align: left; padding: 2.2mm 2mm; }
    td { border: 1px solid #bbb; padding: 2.5mm 2mm; vertical-align: top; line-height: 1.25; overflow-wrap: anywhere; }
    .index { width: 7mm; text-align: center; color: #555; }
    .order { font-weight: bold; }
    .crew { vertical-align: middle; font-weight: 600; background: #f3f4f6; }
    tr.group-start td { border-top: 2px solid #555; }
    .muted { color: #777; font-style: italic; }
    .empty { text-align: center; padding: 18mm 0; color: #777; }
    col.index-col { width: 8mm; }
    col.order-col { width: 34mm; }
    col.contractor-col { width: ${isWarehouse ? '45mm' : '55mm'}; }
    col.installers-col { width: auto; }
    col.wh-col { width: 36mm; }
    @media print {
      @page { size: A4 portrait; margin: 0; }
      .page { min-height: 297mm; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      tr { break-inside: avoid; page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <main class="page">
    <div class="header">
      <div>
        <h1>Plan montaży${isWarehouse ? ' — Magazyn' : ''}</h1>
        <div class="date">${esc(formatDateShort(day))}</div>
      </div>
      <div class="meta">Liczba zleceń: ${dayInstallations.length}<br>Wygenerowano: ${esc(generatedAt)}</div>
    </div>
    <table>
      <colgroup>
        <col class="index-col">
        <col class="order-col">
        <col class="contractor-col">
        <col class="installers-col">
        ${isWarehouse ? '<col class="wh-col">' : ''}
      </colgroup>
      <thead>
        <tr>
          <th>#</th>
          <th>Nr zlecenia</th>
          <th>Kontrahent</th>
          <th>${isWarehouse ? 'Brygadzista' : 'Montażyści'}</th>
          ${isWarehouse ? '<th>WH</th>' : ''}
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
  </main>
</body>
</html>`;

  printHtmlInFrame(html, `Plan montaży ${day}`);
}

export function printDailyInstallationAmounts(installations = [], teams = [], installers = [], day, settlements = {}) {
  if (!day) return;

  const dayInstallations = installations.filter((installation) => {
    if (installation?.planned_dates?.length) return installation.planned_dates.includes(day);
    if (!installation?.planned_start_date) return false;
    return day >= installation.planned_start_date
      && day <= (installation.planned_end_date || installation.planned_start_date);
  });
  const groups = new Map();

  dayInstallations.forEach((installation, sourceIndex) => {
    const assignments = (installation.assignments || []).filter((assignment) => assignment.assigned_date === day);
    const teamAssignments = assignments.filter((assignment) => assignment.team_id);
    const regularAssignments = teamAssignments.filter((assignment) =>
      !teams.find((team) => team.id === assignment.team_id)?.is_special_truck
    );
    const visibleTeamAssignments = regularAssignments.length ? regularAssignments : teamAssignments;
    const teamIds = [...new Set(visibleTeamAssignments.map((assignment) => assignment.team_id))];
    const directAssignments = assignments.filter((assignment) =>
      !assignment.team_id && (assignment.installer_id || assignment.installer_name)
    );
    const mergeDirectWithOnlyTeam = teamIds.length === 1 && directAssignments.length > 0;
    const contexts = teamIds.map((teamId) => ({ teamId, key: `team:${teamId}` }));
    if (!teamIds.length || (teamIds.length > 1 && directAssignments.length)) {
      contexts.push({ teamId: null, key: directAssignments.length ? 'individual' : 'unassigned' });
    }

    contexts.forEach(({ teamId, key }) => {
      const team = teamId == null ? null : teams.find((candidate) => candidate.id === teamId);
      const groupAssignments = teamId == null
        ? assignments.filter((assignment) => !assignment.team_id)
        : assignments.filter((assignment) => assignment.team_id === teamId)
          .concat(mergeDirectWithOnlyTeam ? directAssignments : []);
      const group = groups.get(key) || {
        label: team?.name || groupAssignments[0]?.team_name
          || (teamId != null ? `Ekipa ${teamId}` : key === 'individual' ? 'Przypisania indywidualne' : 'Nieprzypisane'),
        order: team ? teams.indexOf(team) : teams.length + (key === 'unassigned' ? 1 : 0),
        rows: [],
      };
      const settlement = settlements[installation.id];
      const matchingDays = (settlement?.days || []).filter((item) =>
        item.date === day && (
          (item.team_id ?? null) === teamId
          || (mergeDirectWithOnlyTeam && teamId != null && item.team_id == null)
        )
      );
      const settlementWorkers = matchingDays.flatMap((item) => (item.installers || []).map((worker) => {
        const addition = (settlement?.saved_additional_amounts || []).find((saved) =>
          saved.assigned_date === day
          && (saved.team_id ?? null) === (item.team_id ?? null)
          && String(saved.installer_id) === String(worker.installer_id)
        );
        return { ...worker, amount: addition ? addition.final_day_amount : worker.amount };
      }));
      if (!settlement?.days?.length && installationDates(installation).length === 1 && teamIds.length <= 1) {
        settlementWorkers.push(...(settlement?.installers || []));
      }
      const workers = new Map();
      function addWorker(id, name, amount = null) {
        const workerKey = id == null ? `name:${name}` : `id:${id}`;
        const existing = workers.get(workerKey);
        if (existing) {
          if (amount != null) existing.amount = (existing.amount ?? 0) + Number(amount);
          return;
        }
        workers.set(workerKey, { id, name, amount: amount == null ? null : Number(amount) });
      }
      groupAssignments.forEach((assignment) => {
        if (assignment.installer_id) {
          const installer = installers.find((item) => item.id === assignment.installer_id);
          addWorker(assignment.installer_id, assignment.installer_name
            || (installer ? `${installer.first_name} ${installer.last_name}` : 'Pracownik'));
        } else if (assignment.team_id) {
          (team?.members || []).forEach((member) => addWorker(member.id, `${member.first_name} ${member.last_name}`));
        } else if (assignment.installer_name) {
          addWorker(null, assignment.installer_name);
        }
      });
      settlementWorkers.forEach((worker) => addWorker(worker.installer_id, worker.installer_name, worker.amount));
      const people = [...workers.values()];
      group.rows.push({
        installation,
        people,
        sourceIndex,
        displayOrder: groupAssignments.length
          ? Math.min(...groupAssignments.map((assignment) => assignment.display_order ?? 0))
          : 0,
      });
      groups.set(key, group);
    });
  });

  const money = new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const groupedRows = [...groups.values()].sort((left, right) =>
    left.order - right.order || left.label.localeCompare(right.label, 'pl')
  );
  const rowsHtml = groupedRows.length ? groupedRows.map((group) => {
    const orderRows = group.rows.sort((left, right) =>
      left.displayOrder - right.displayOrder || left.sourceIndex - right.sourceIndex
    ).map(({ installation, people }) => {
      const number = installation.calendar_status === 'calendar_note'
        ? installation.calendar_note_title || installation.notes || 'Notatka'
        : installation.order_number;
      const peopleHtml = people.length ? people.map((worker) => `
        <div class="person"><span>${esc(worker.name)}</span><strong>${worker.amount == null || !Number.isFinite(worker.amount)
          ? '—' : `${esc(money.format(worker.amount))} PLN`}</strong></div>`).join('')
        : '<span class="muted">Brak przypisanych osób</span>';
      return `<tr><td class="order">${esc(number) || '<span class="muted">—</span>'}</td><td>${peopleHtml}</td></tr>`;
    }).join('');
    return `<tbody class="team-group"><tr class="team-heading"><th colspan="2">${esc(group.label)}</th></tr>${orderRows}</tbody>`;
  }).join('') : '<tbody><tr><td colspan="2" class="empty">Brak zleceń montażowych w tym dniu</td></tr></tbody>';
  const now = new Date();
  const generatedAt = `${now.getDate()} ${POLISH_MONTHS[now.getMonth()]} ${now.getFullYear()}, ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const html = `<!DOCTYPE html>
<html lang="pl"><head><meta charset="UTF-8"><title>Wydruk z kwotami ${esc(day)}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 10pt; }
  .page { padding: 8mm 9mm; }
  .header { display: flex; justify-content: space-between; align-items: flex-end; gap: 10mm; margin-bottom: 5mm; border-bottom: 2px solid #111; padding-bottom: 3mm; }
  h1 { margin: 0; font-size: 16pt; line-height: 1.1; }
  .date { font-size: 11pt; font-weight: bold; margin-top: 2mm; }
  .meta { font-size: 7.5pt; color: #666; text-align: right; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  th { text-align: left; }
  thead th { background: #111; color: #fff; font-size: 8.5pt; padding: 2.2mm 2mm; }
  td { border: 1px solid #bbb; padding: 2.5mm 2mm; vertical-align: top; line-height: 1.3; overflow-wrap: anywhere; }
  .order { width: 42mm; font-weight: bold; }
  .team-heading th { padding: 2.5mm 2mm; background: #e5e7eb; border: 1px solid #aaa; font-size: 10pt; }
  .person { display: flex; justify-content: space-between; gap: 5mm; padding: 1mm 0; }
  .person + .person { border-top: 1px solid #e5e7eb; }
  .person strong { white-space: nowrap; }
  .muted { color: #777; font-style: italic; }
  .empty { text-align: center; padding: 18mm 0; color: #777; }
  @media print { @page { size: A4 portrait; margin: 0; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } tr { break-inside: avoid; page-break-inside: avoid; } }
</style></head><body><main class="page">
  <div class="header"><div><h1>Wydruk z kwotami</h1><div class="date">${esc(formatDateShort(day))}</div></div>
    <div class="meta">Liczba zleceń: ${dayInstallations.length}<br>Wygenerowano: ${esc(generatedAt)}</div></div>
  <table><colgroup><col style="width:42mm"><col></colgroup><thead><tr><th>Nr zlecenia</th><th>Przypisani i kwota końcowa</th></tr></thead>${rowsHtml}</table>
</main></body></html>`;
  printHtmlInFrame(html, `Wydruk z kwotami ${day}`);
}

export function printInstallation(installation, teams, installers = [], filterDate = null) {
  if (!installation) return;

  const equipment = [];
  if (installation.rusztowanie) equipment.push('Rusztowanie');
  if (installation.drabina) equipment.push('Drabina');
  if (installation.pajaczek) equipment.push('Pajaczek');
  if (installation.agregat) equipment.push('Agregat');
  if (installation.dodatkowy_bus) equipment.push('Dodatkowy bus');
  if (installation.tir) equipment.push('TIR');

  const equipmentHtml = equipment.length
    ? equipment.map(e => `<span class="equipment-item">${e}</span>`).join('')
    : '<span class="no-value">Brak</span>';

  const dateRange = installation.planned_dates?.length
    ? installationDates(installation).map(formatDate).join(', ')
    : installation.planned_start_date === installation.planned_end_date
    ? formatDate(installation.planned_start_date)
    : `${formatDate(installation.planned_start_date)} – ${formatDate(installation.planned_end_date)}`;

  const timeStr = installation.planned_start_time
    ? ` (godz. ${installation.planned_start_time.slice(0, 5)})`
    : '';

  // Build assignments per day
  let days = filterDate
    ? [filterDate]
    : installationDates(installation);
  const assignments = installation.assignments || [];

  // Group assignment records by team_id per day
  let assignmentRowsHtml = '';

  if (days.length === 0 && assignments.length > 0) {
    // No date range set, just list all assignments
    days.push(...[...new Set(assignments.map(a => a.assigned_date))].sort());
  }

  // Zbierz wszystkich montażystów ze wszystkich dni (globalnie zdeduplikowanych)
  const allPeople = [];
  const seenIds = new Set();

  function getInstallerMeta(installerId) {
    const inst = installers.find(i => i.id === installerId);
    return { position: inst?.position || null, rank: inst?.position_rank ?? 9999 };
  }

  function formatName(name, position) {
    return position
      ? `${esc(name)} <span style="color:#666">(${esc(position)})</span>`
      : esc(name);
  }

  for (const day of days) {
    const dayAssignments = assignments.filter(a => a.assigned_date === day);

    // Group by team_id
    const teamGroups = {};
    const individuals = [];
    dayAssignments.forEach(a => {
      if (a.team_id) {
        if (!teamGroups[a.team_id]) teamGroups[a.team_id] = { teamName: a.team_name, records: [] };
        teamGroups[a.team_id].records.push(a);
      } else {
        individuals.push(a);
      }
    });

    for (const [teamId, group] of Object.entries(teamGroups)) {
      const isFullTeam = group.records.some(r => !r.installer_id);
      const teamDef = teams ? teams.find(t => t.id === Number(teamId)) : null;
      if (isFullTeam && teamDef) {
        for (const m of teamDef.members) {
          if (!seenIds.has(m.id)) {
            seenIds.add(m.id);
            allPeople.push({ html: formatName(`${m.first_name} ${m.last_name}`, m.position), rank: m.position_rank ?? 9999 });
          }
        }
      } else {
        for (const r of group.records.filter(r => r.installer_id)) {
          if (!seenIds.has(r.installer_id)) {
            seenIds.add(r.installer_id);
            const memberDef = teamDef?.members.find(m => m.id === r.installer_id);
            const position = memberDef?.position || getInstallerMeta(r.installer_id).position;
            const rank = memberDef?.position_rank ?? getInstallerMeta(r.installer_id).rank;
            allPeople.push({ html: formatName(r.installer_name || '–', position), rank });
          }
        }
      }
    }
    for (const a of individuals) {
      if (a.installer_id && !seenIds.has(a.installer_id)) {
        seenIds.add(a.installer_id);
        const { position, rank } = getInstallerMeta(a.installer_id);
        allPeople.push({ html: formatName(a.installer_name || '–', position), rank });
      } else if (!a.installer_id) {
        allPeople.push({ html: esc(a.installer_name) || '–', rank: 9999 });
      }
    }
  }

  // Sortuj według rangi stanowiska
  allPeople.sort((a, b) => a.rank - b.rank);

  if (allPeople.length === 0) {
    assignmentRowsHtml = `
      <tr>
        <td class="no-value">Brak przypisanych osób</td>
      </tr>`;
  } else {
    allPeople.forEach(person => {
      assignmentRowsHtml += `
      <tr>
        <td>${person.html}</td>
      </tr>`;
    });
  }

  if (!assignmentRowsHtml) {
    assignmentRowsHtml = '<tr><td colspan="3" class="no-value">Brak przypisań</td></tr>';
  }

  const now = new Date();
  const generatedAt = `${now.getDate()} ${POLISH_MONTHS[now.getMonth()]} ${now.getFullYear()}, ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;

  const html = `<!DOCTYPE html>
<html lang="pl">
<head>
  <meta charset="UTF-8">
  <title>Zlecenie ${esc(installation.order_number)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; padding: 20mm 18mm; }
    h1 { font-size: 18pt; font-weight: bold; margin-bottom: 2px; }
    .order-number { font-size: 12pt; color: #555; margin-bottom: 20px; }
    .section { margin-bottom: 18px; }
    .section-title {
      font-size: 11pt; font-weight: bold; text-transform: uppercase;
      letter-spacing: 0.05em; border-bottom: 2px solid #000;
      padding-bottom: 4px; margin-bottom: 10px;
    }
    .grid { display: grid; grid-template-columns: 160px 1fr; row-gap: 5px; }
    .label { font-weight: bold; color: #444; }
    .equipment-list { display: flex; gap: 10px; flex-wrap: wrap; }
    .equipment-item {
      background: #f0f0f0; border: 1px solid #ccc;
      padding: 3px 10px; border-radius: 4px; font-size: 10pt;
    }
    .no-value { color: #999; font-style: italic; }
    table { width: 100%; border-collapse: collapse; font-size: 10pt; }
    thead th {
      background: #222; color: #fff; padding: 7px 10px;
      text-align: left; font-weight: bold;
    }
    td { padding: 5px 10px; border-bottom: 1px solid #ddd; vertical-align: top; }
    .day-header td {
      background: #e8e8e8; font-weight: bold; font-size: 10pt;
      border-top: 2px solid #aaa; border-bottom: 1px solid #aaa;
      padding: 6px 10px;
    }
    tbody tr:not(.day-header):hover td { background: #fafafa; }
    .signature-row {
      display: flex; justify-content: flex-end; margin-top: 40px;
    }
    .signature-box {
      text-align: center; width: 220px;
    }
    .signature-line {
      border-top: 1px solid #000; margin-bottom: 6px;
    }
    .signature-label { font-size: 9pt; color: #555; }
    .footer { margin-top: 20px; font-size: 8pt; color: #aaa; border-top: 1px solid #eee; padding-top: 8px; }
    @media print {
      body { padding: 0; }
      @page { size: A4 portrait; margin: 15mm 18mm; }
    }
  </style>
</head>
<body>
  <h1>Zlecenie montażowe</h1>
  <div class="order-number">Nr zlecenia: <strong>${esc(installation.order_number)}</strong></div>

  <div class="section">
    <div class="section-title">Dane zlecenia</div>
    <div class="grid">
      <span class="label">Kontrahent:</span>
      <span>${esc(installation.contractor) || '<span class="no-value">–</span>'}</span>

      <span class="label">Adres:</span>
      <span>${esc(installation.address) || '<span class="no-value">–</span>'}</span>

      <span class="label">Data montażu:</span>
      <span>${esc(dateRange)}${esc(timeStr)}</span>

      ${filterDate ? `<span class="label">Drukowane dla dnia:</span><span><strong>${esc(formatDateShort(filterDate))}</strong></span>` : ''}

      <span class="label">WH:</span>
      <span>${esc(installation.wh) || '<span class="no-value">–</span>'}</span>

      ${installation.notes ? `<span class="label">Uwagi:</span><span>${esc(installation.notes)}</span>` : ''}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Wyposażenie</div>
    <div class="equipment-list">${equipmentHtml}</div>
  </div>

  <div class="section">
    <div class="section-title">Montażyści</div>
    <table>
      <tbody>${assignmentRowsHtml}</tbody>
    </table>
  </div>

  <div class="footer">Wygenerowano: ${generatedAt}</div>

  <div class="signature-row">
    <div class="signature-box">
      <div class="signature-line"></div>
      <div class="signature-label">Podpis osoby zatwierdzającej</div>
    </div>
  </div>

  <script>
    window.onload = function() { window.print(); };
  </script>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=850,height=1100');
  if (win) {
    win.opener = null;
    win.document.write(html);
    win.document.close();
  } else {
    // Popup zablokowany lub niedostępny – fallback przez Blob URL
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }
}
